import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import ProgressSidebar from './ProgressSidebar';
import {
  ExamTypeStep,
  CurrentLevelStep,
  GoalsStep,
  SubjectStrengthStep,
  StudyPatternsStep,
  CommitmentsStep
} from './steps';
import StudyPlanResults from './StudyPlanResults';
import { StudyPlanData, StudyPlanStep } from '../../types/study-plan';
import './StudyPlanWizard.css';

const StudyPlanWizard: React.FC = () => {
  const [currentStep, setCurrentStep] = useState(1);
  const [studyPlanData, setStudyPlanData] = useState<Partial<StudyPlanData>>({});
  const [isCompleted, setIsCompleted] = useState(false);

  const steps: StudyPlanStep[] = [
    { id: 1, title: 'Exam Type', description: 'Select your target exam', completed: false, current: true },
    { id: 2, title: 'Current Level', description: 'Assess your preparation stage', completed: false, current: false },
    { id: 3, title: 'Goals', description: 'Define your target scores', completed: false, current: false },
    { id: 4, title: 'Subject Strength', description: 'Evaluate your subjects', completed: false, current: false },
    { id: 5, title: 'Study Patterns', description: 'Set your schedule preferences', completed: false, current: false },
    { id: 6, title: 'Commitments', description: 'Add your other activities', completed: false, current: false }
  ];

  const [stepStates, setStepStates] = useState(steps);

  const updateStepStates = (stepNumber: number) => {
    setStepStates(prev => prev.map(step => ({
      ...step,
      completed: step.id < stepNumber,
      current: step.id === stepNumber
    })));
  };

  const handleNext = (data: any) => {
    setStudyPlanData(prev => ({ ...prev, ...data }));
    
    if (currentStep < steps.length) {
      const nextStep = currentStep + 1;
      setCurrentStep(nextStep);
      updateStepStates(nextStep);
    } else {
      // All steps completed
      setStepStates(prev => prev.map(step => ({ ...step, completed: true, current: false })));
      setIsCompleted(true);
    }
  };

  const handleBack = () => {
    if (currentStep > 1) {
      const prevStep = currentStep - 1;
      setCurrentStep(prevStep);
      updateStepStates(prevStep);
    }
  };

  const handleStepClick = (stepNumber: number) => {
    // Allow navigation only to completed steps or current step
    const targetStep = stepStates.find(s => s.id === stepNumber);
    if (targetStep && (targetStep.completed || targetStep.current)) {
      setCurrentStep(stepNumber);
      updateStepStates(stepNumber);
    }
  };

  const handleRestart = () => {
    setCurrentStep(1);
    setStudyPlanData({});
    setIsCompleted(false);
    updateStepStates(1);
  };

  const renderCurrentStep = () => {
    if (isCompleted) {
      return (
        <StudyPlanResults 
          studyPlanData={studyPlanData as StudyPlanData}
          onRestart={handleRestart}
        />
      );
    }

    switch (currentStep) {
      case 1:
        return (
          <ExamTypeStep
            onNext={handleNext}
            defaultValue={studyPlanData.examType}
          />
        );
      case 2:
        return (
          <CurrentLevelStep
            onNext={handleNext}
            onBack={handleBack}
            examType={studyPlanData.examType}
            defaultValue={studyPlanData.currentLevel ? { 
              currentLevel: studyPlanData.currentLevel,
              currentScore: (studyPlanData as any).currentScore 
            } : undefined}
          />
        );
      case 3:
        return (
          <GoalsStep
            onNext={handleNext}
            onBack={handleBack}
            examType={studyPlanData.examType || ''}
            defaultValue={{
              targetScore: studyPlanData.targetScore,
              subjectScores: studyPlanData.subjectScores
            }}
          />
        );
      case 4:
        return (
          <SubjectStrengthStep
            onNext={handleNext}
            onBack={handleBack}
            examType={studyPlanData.examType || ''}
            defaultValue={studyPlanData.subjectStrengths ? { subjectStrengths: studyPlanData.subjectStrengths } : undefined}
          />
        );
      case 5:
        return (
          <StudyPatternsStep
            onNext={handleNext}
            onBack={handleBack}
            defaultValue={
              studyPlanData.studyPattern && studyPlanData.sleepPattern
                ? { studyPattern: studyPlanData.studyPattern, sleepPattern: studyPlanData.sleepPattern }
                : undefined
            }
          />
        );
      case 6:
        return (
          <CommitmentsStep
            onNext={handleNext}
            onBack={handleBack}
            defaultValue={studyPlanData.commitments ? { commitments: studyPlanData.commitments } : undefined}
          />
        );
      default:
        return null;
    }
  };

  return (
    <div className="study-plan-wizard">
      {/* Progress Sidebar - Fixed on Desktop */}
      <div className="d-none d-lg-block">
        <div className="sidebar-container">
          <ProgressSidebar 
            steps={stepStates} 
            currentStep={currentStep} 
            onStepClick={handleStepClick}
          />
        </div>
      </div>

      {/* Mobile Progress Header */}
      <div className="d-lg-none">
        <div className="mobile-progress-header bg-primary text-white p-3">
          <div className="d-flex justify-content-between align-items-center">
            <h6 className="mb-0 text-white">Study Plan Setup</h6>
            <span className="badge bg-light text-primary">
              Step {currentStep} of {steps.length}
            </span>
          </div>
          <div className="progress mt-2" style={{ height: '4px' }}>
            <div 
              className="progress-bar bg-light" 
              role="progressbar" 
              style={{ width: `${(currentStep / steps.length) * 100}%` }}
            ></div>
          </div>
          <small className="text-white opacity-75 mt-1 d-block">
            {stepStates.find(s => s.id === currentStep)?.title}
          </small>
        </div>
      </div>

      {/* Main Content - Scrollable */}
      <div className="wizard-content-container">
        <div className="wizard-content">
          <AnimatePresence mode="wait">
            <motion.div
              key={isCompleted ? 'results' : currentStep}
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              transition={{ duration: 0.3 }}
            >
              {renderCurrentStep()}
            </motion.div>
          </AnimatePresence>
        </div>
      </div>
    </div>
  );
};

export default StudyPlanWizard;